// chatbotEngine.js
//
// Knowledge base + intent matching engine for the SportsForEquity site assistant.
//
// Architecture note for future LLM integration:
// -----------------------------------------------------------------------------
// The exported `getResponse(userText, history)` function is the single entry point.
// To plug in a real LLM (OpenAI, Anthropic, etc.) later, you have two options:
//
//   1. Replace the body of `getResponse` with a fetch() call to your serverless
//      endpoint (Netlify/Vercel/Cloudflare function) that holds the API key.
//      Pass `userText`, `history`, and `SITE_KNOWLEDGE` to the endpoint as context.
//
//   2. Keep this engine as a fast fallback, and only call the LLM when the
//      confidence score from `findBestIntent` is below a threshold.
//
// Do NOT put an API key directly in this file — it would be visible to anyone
// viewing your site's source.
// -----------------------------------------------------------------------------

// Compact site knowledge — also useful as system-prompt context if you wire up an LLM.
export const SITE_KNOWLEDGE = {
  org: "SportsForEquity",
  mission:
    "Empowering health equity and wellness through table tennis — neuro-wellness programs, healthcare fundraising, and youth athlete empowerment.",
  email: "info@sportsforequity.live",
  metrics: {
    parkinsonSessions: 72,
    nationalAthletes: 12,
    athletesEmpowered: 27,
    chapters: 10,
  },
  programs: [
    "In-Person Parkinson's Sessions",
    "Community Outreach",
    "Health Equity Support (cancer care fundraising)",
    "Youth Athlete Training",
  ],
  chapters: [
    "Seattle, WA",
    "Los Angeles, CA",
    "Tri-Valley, CA",
    "San Jose, CA",
    "Fremont, CA",
    "Phoenix, AZ",
    "Houston, TX",
    "New Jersey",
    "San Diego, CA",
    "Milpitas, CA",
  ],
};

// ─── Normalization helpers ──────────────────────────────────────────────────
const normalize = (text) =>
  text
    .toLowerCase()
    .replace(/[‘’']/g, "")  // strip straight + curly apostrophes
    .replace(/[^\w\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

// Common stop-words that shouldn't drive matching
const STOPWORDS = new Set([
  "a", "an", "the", "is", "are", "was", "were", "be", "been", "being",
  "do", "does", "did", "can", "could", "would", "should", "will", "shall",
  "i", "you", "we", "they", "he", "she", "it", "me", "us", "them",
  "to", "of", "for", "in", "on", "at", "by", "with", "from", "about",
  "and", "or", "but", "if", "so", "than", "that", "this", "these", "those",
  "my", "your", "our", "their", "his", "her", "its",
  "have", "has", "had", "get", "got",
  "what", "when", "where", "who", "why", "how", "which", "tell",
  "please", "thanks", "thank", "hi", "hello", "hey",
]);

const tokens = (text) =>
  normalize(text)
    .split(" ")
    .filter((t) => t && !STOPWORDS.has(t));

// ─── Intents ────────────────────────────────────────────────────────────────
//
// Each intent declares:
//   id          unique key
//   phrases     multi-word patterns (higher weight when matched whole)
//   keywords    individual signal words
//   answer      response text (supports **bold**, [text](url), and bare URLs)
//   suggestions follow-up chips shown under the answer
//
// Scoring: phrases score (2 + word_count); keywords score 1 each. The intent
// with the highest score above the threshold wins.

const INTENTS = [
  {
    id: "greeting",
    phrases: ["good morning", "good afternoon", "good evening", "hi", "hello", "hey", "howdy"],
    keywords: ["greetings"],
    answer:
      "Hi there! 👋 I'm the SportsForEquity assistant. I can help you learn about our programs, chapters, events, team, and how to get involved.",
    suggestions: [
      "What is SportsForEquity?",
      "Tell me about your programs",
      "How can I volunteer?",
    ],
  },
  {
    id: "about",
    phrases: [
      "what is sportsforequity",
      "what is sports for equity",
      "who are you",
      "what do you do",
      "your mission",
      "sports for equity",
    ],
    keywords: ["mission", "about", "purpose", "organization", "vision"],
    answer:
      "**SportsForEquity** empowers health equity and wellness through table tennis. Our work spans three pillars:\n\n• **Neuro-wellness** — in-person Parkinson's sessions that improve motor skills and confidence\n• **Health-equity support** — fundraising for cancer care and underserved healthcare\n• **Youth empowerment** — training and mentoring junior athletes\n\nOur mission aligns with United Nations SDG 3 (Good Health & Well-Being) and SDG 10 (Reduced Inequalities).",
    suggestions: [
      "What programs do you offer?",
      "Where are your chapters?",
      "What's your impact so far?",
    ],
  },
  {
    id: "programs",
    phrases: ["what programs", "your programs", "list of programs", "what do you offer"],
    keywords: ["programs", "services", "activities", "offerings"],
    answer:
      "We run four ongoing programs:\n\n• **In-Person Parkinson's Sessions** — weekly table tennis sessions designed for people with Parkinson's\n• **Community Outreach** — events that bring table tennis to schools, senior centers, and community spaces\n• **Health Equity Support** — fundraising for cancer care and healthcare access\n• **Youth Athlete Training** — coaching and competitive opportunities for junior players\n\nVisit the Home page to see the schedule and register for any of these.",
    suggestions: [
      "Tell me about the Parkinson's program",
      "What is Youth Empowerment?",
      "How do I register?",
    ],
  },
  {
    id: "parkinsons",
    phrases: [
      "parkinson's program",
      "parkinsons program",
      "parkinson session",
      "parkinsons session",
      "register for a parkinson",
      "register for parkinson",
      "ping pong parkinson",
      "table tennis for parkinson",
    ],
    keywords: ["parkinson", "parkinsons", "neurological", "movement", "tremor"],
    answer:
      "Our **Parkinson's program** uses table tennis as a therapeutic tool — research suggests it improves hand-eye coordination, reaction time, balance, and quality of life for people living with Parkinson's. We run weekly in-person sessions across our chapters, free of charge.\n\nSessions are open to people at any stage; caregivers are welcome too. Check the Programs → Parkinson Session page for the current schedule.",
    suggestions: [
      "Where are the sessions held?",
      "How do I register?",
      "Who teaches the sessions?",
    ],
  },
  {
    id: "session-locations",
    phrases: [
      "where are the sessions held",
      "where are the sessions",
      "where can i attend",
      "where to attend",
      "where do i attend",
      "session locations",
      "session location",
      "where do you hold sessions",
      "where do sessions take place",
      "in person session location",
      "in person sessions location",
      "session schedule",
    ],
    keywords: ["attend"],
    answer:
      "Our **in-person Parkinson's sessions** are held at:\n\n" +
      "• **Seattle Pacific Table Tennis Club**\n" +
      "   13249 NE 20th St, Bellevue, WA 98005\n" +
      "   Saturdays 9:00 – 10:30 AM\n\n" +
      "• **Los Angeles Table Tennis Sessions**\n" +
      "   Sundays 5:30 PM – 7:00 PM\n\n" +
      "Check the Programs → Parkinson Session page for the next session date and to register.",
    suggestions: [
      "How do I register?",
      "Tell me about the Parkinson's program",
      "Where are your chapters?",
    ],
  },
  {
    id: "youth",
    phrases: ["youth program", "youth empowerment", "junior athletes", "kids program"],
    keywords: ["youth", "junior", "kids", "children", "students", "training"],
    answer:
      "**Youth Empowerment** trains and mentors junior athletes — building competitive table tennis skills, leadership, and community involvement. Many of our youth athletes go on to start school clubs, volunteer at Parkinson's sessions, and represent SportsForEquity at events.\n\nThe Youth Empowerment page lists active chapters and how to join.",
    suggestions: [
      "Who can join?",
      "Where do you train?",
      "Tell me about your chapters",
    ],
  },
  {
    id: "community-outreach",
    phrases: ["community outreach", "outreach program", "community events"],
    keywords: ["outreach", "community", "schools", "seniors"],
    answer:
      "**Community Outreach** brings table tennis to schools, senior centers, and community spaces — hosted by SportsForEquity chapters and volunteer members. Each event focuses on inclusion, intergenerational connection, and introducing new people to the sport.",
    suggestions: [
      "How do I attend an event?",
      "Can I host an outreach event?",
      "Where are your chapters?",
    ],
  },
  {
    id: "health-equity",
    phrases: ["health equity", "healthcare support", "cancer support", "cancer care"],
    keywords: ["health", "equity", "healthcare", "cancer", "fundraising", "fundraise"],
    answer:
      "**Health Equity Support** is our ongoing fundraising initiative for cancer care and equitable healthcare access. We partner with healthcare nonprofits and run sponsored events to direct funds where they're most needed.\n\nVisit Programs → Health Equity Support to see current campaigns and donate.",
    suggestions: [
      "How can I donate?",
      "Who do you partner with?",
      "Where does the money go?",
    ],
  },
  {
    id: "chapters",
    phrases: ["your chapters", "list of chapters", "where are you located", "locations"],
    keywords: ["chapters", "chapter", "locations", "branches", "cities", "where"],
    answer:
      "We currently have **10 chapters** across the U.S.:\n\n• **Seattle, WA**\n• **Los Angeles, CA**\n• **Tri-Valley, CA**\n• **San Jose, CA**\n• **Fremont, CA**\n• **Phoenix, AZ**\n• **Houston, TX**\n• **New Jersey**\n• **San Diego, CA**\n• **Milpitas, CA**\n\nEach chapter runs its own sessions and events — visit the Events page for what's coming up.",
    suggestions: [
      "Tell me about the Seattle chapter",
      "Tell me about the Milpitas chapter",
      "How do I start a new chapter?",
    ],
  },
  {
    id: "chapter-seattle",
    phrases: ["seattle chapter", "seattle wa", "seattle location", "seattle"],
    keywords: ["washington"],
    answer:
      "The **Seattle chapter** was founded by Saicharan Vellanki. It hosts weekly Parkinson's sessions, youth training, and community outreach across the greater Seattle area. Past activity includes participation in the Walk for Parkinson's at Husky Stadium with the Northwest Parkinson's Foundation.",
    suggestions: [
      "What events are in Seattle?",
      "How do I join the Seattle chapter?",
      "Tell me about other chapters",
    ],
  },
  {
    id: "chapter-houston",
    phrases: ["houston chapter", "houston tx", "houston"],
    keywords: ["texas"],
    answer:
      "The **Houston chapter** is co-presided by Joshua and Isabella Joseph. The chapter focuses on community outreach, youth training, and partnerships with local healthcare initiatives.",
    suggestions: [
      "What events are in Houston?",
      "Tell me about other chapters",
      "Who's on the team?",
    ],
  },
  {
    id: "chapter-los-angeles",
    phrases: ["los angeles chapter", "los angeles ca", "la chapter", "los angeles"],
    keywords: [],
    answer:
      "The **Los Angeles, CA chapter** is founded and led by **SriPrahan Tellagaddala**. The chapter focuses on community outreach, youth training, and bringing table tennis to underserved communities in the LA area.",
    suggestions: [
      "Tell me about other chapters",
      "Who's on the core team?",
      "How can I get involved?",
    ],
  },
  {
    id: "chapter-trivalley",
    phrases: ["tri-valley chapter", "tri-valley", "trivalley"],
    keywords: [],
    answer:
      "The **Tri-Valley, CA chapter** is co-founded and co-led by **Amritshyaam Gautam** and **Raghav Shah**. Recent activities include a multi-day camp in April 2026 and ongoing community sessions.",
    suggestions: [
      "Tell me about other chapters",
      "How do I start a chapter?",
      "Who's on the core team?",
    ],
  },
  {
    id: "chapter-sanjose",
    phrases: ["san jose chapter", "san jose ca", "san jose"],
    keywords: [],
    answer:
      "The **San Jose, CA chapter** is founded and led by **Sumedh Seetharaman**. Sumedh is a competitive player and mentor who uses the chapter to support youth development and raise funds for underprivileged children.",
    suggestions: [
      "Tell me about other chapters",
      "Who's on the core team?",
      "How can I get involved?",
    ],
  },
  {
    id: "chapter-fremont",
    phrases: ["fremont chapter", "fremont ca", "fremont"],
    keywords: [],
    answer:
      "The **Fremont, CA chapter** is co-founded and co-led by brothers **Shriraam Velayutham** and **Sivaraam Velayutham** — both elite athletes who have represented the U.S. national team. Alongside competing, they regularly coach younger players and run seasonal camps.",
    suggestions: [
      "Tell me about other chapters",
      "Who's on the core team?",
      "How can I get involved?",
    ],
  },
  {
    id: "chapter-phoenix",
    phrases: ["phoenix chapter", "phoenix az", "phoenix", "arizona"],
    keywords: ["arizona"],
    answer:
      "The **Phoenix, AZ chapter** is founded and led by **Aditya Bhaskarpandit**. Aditya coaches kids and adults, runs a high school table tennis club, and has sponsored junior tournaments across Arizona to grow the sport locally.",
    suggestions: [
      "Tell me about other chapters",
      "Who's on the core team?",
      "How can I get involved?",
    ],
  },
  {
    id: "chapter-newjersey",
    phrases: ["new jersey chapter", "new jersey", "nj chapter"],
    keywords: [],
    answer:
      "The **New Jersey chapter** is founded and led by **Ishan Rangdal** (President), with **Riaan Lodha** (Vice President) and **Yug Sinha** (Secretary). Based in Livingston, NJ, the chapter brings together competitive junior players committed to growing table tennis and supporting underserved communities.",
    suggestions: [
      "Tell me about other chapters",
      "Who's on the core team?",
      "How can I get involved?",
    ],
  },
  {
    id: "chapter-sandiego",
    phrases: ["san diego chapter", "san diego ca", "san diego"],
    keywords: [],
    answer:
      "The **San Diego, CA chapter** is founded and led by **Ashwin Kodali**. Ashwin trained under Hall of Fame coaches Stellan and Angie Bengtsson and is committed to growing the sport in Southern California through SportsForEquity.",
    suggestions: [
      "Tell me about other chapters",
      "Who's on the core team?",
      "How can I get involved?",
    ],
  },
  {
    id: "chapter-milpitas",
    phrases: ["milpitas chapter", "milpitas ca", "milpitas"],
    keywords: [],
    answer:
      "The **Milpitas, CA chapter** is co-founded and co-led by **Siddharth Kodaty** and **Shriya Kodaty**.\n\n**Siddharth** is a competitive player with 4 years of experience who volunteers at ICC Table Tennis Center mentoring children. He attends Irvington High School.\n\n**Shriya** started playing at age 9 and is a WTT U11 Champion (2025). She is also involved in scouting and community service projects.\n\nBoth are passionate about using table tennis to promote health and serve their community.",
    suggestions: [
      "Tell me about other chapters",
      "Who's on the core team?",
      "How can I get involved?",
    ],
  },
  {
    id: "events",
    phrases: ["upcoming events", "your events", "what events", "next event"],
    keywords: ["events", "event", "tournament", "tournaments", "calendar", "schedule"],
    answer:
      "Our **Events** page lists everything coming up across all chapters — Parkinson's sessions, community outreach days, fundraisers, and tournaments. It's also where we publish photos and recaps from past events.\n\nHead to the Events tab in the top navigation for the full list.",
    suggestions: [
      "Where are your chapters?",
      "How do I register for an event?",
      "What was your last event?",
    ],
  },
  {
    id: "team",
    phrases: ["who is on your team", "your team", "core team", "founders", "leadership"],
    keywords: ["team", "founders", "founder", "leadership", "staff", "members"],
    answer:
      "Our **Core Team** spans 10 chapters across the U.S.:\n\n• **Saicharan Vellanki** — Seattle, WA Founder & President\n• **SriPrahan Tellagaddala** — Los Angeles, CA Founder & President\n• **Amritshyaam Gautam & Raghav Shah** — Tri-Valley, CA Founders & Co-Presidents\n• **Sumedh Seetharaman** — San Jose, CA Founder & President\n• **Shriraam & Sivaraam Velayutham** — Fremont, CA Founders & Co-Presidents\n• **Aditya Bhaskarpandit** — Phoenix, AZ Founder & President\n• **Joshua & Isabella Joseph** — Houston, TX Founders & Co-Presidents\n• **Ishan Rangdal** — New Jersey Founder & President\n• **Ashwin Kodali** — San Diego, CA Founder & President\n• **Siddharth & Shriya Kodaty** — Milpitas, CA Founders & Co-Presidents\n\nSee the About page for full bios.",
    suggestions: [
      "Who's on the advisory board?",
      "How can I join the team?",
      "Tell me about chapters",
    ],
  },
  {
    id: "advisory-board",
    phrases: ["advisory board", "board members", "advisors"],
    keywords: ["advisory", "board", "advisors", "advisor", "mentors"],
    answer:
      "Our **Advisory Board** brings together coaches, healthcare professionals, and community leaders who guide the organization. Members include **Coach Xinxu Zhao** (lead coach and advisor) among others. Full list on the About → Advisory Board page.",
    suggestions: [
      "Tell me about Coach Xinxu",
      "Who's on the core team?",
      "Who are your partners?",
    ],
  },
  {
    id: "contact",
    phrases: ["contact information", "contact you", "how to contact", "reach out", "email address", "linkedin", "linked url", "linkedin url", "linkedin page", "social media", "follow you"],
    keywords: ["contact", "email", "phone", "reach", "address", "linkedin", "linked", "social"],
    answer:
      "You can reach us at **info@sportsforequity.live** — we read every message. The Contact page also has a form for specific inquiries (volunteering, partnerships, press). You can also follow us on [LinkedIn](https://www.linkedin.com/company/sportsforequityorg/) for updates.",
    suggestions: [
      "How can I volunteer?",
      "How do I partner with you?",
      "Where are you located?",
    ],
  },
  {
    id: "volunteer",
    phrases: [
      "how can i volunteer",
      "how to volunteer",
      "register to volunteer",
      "sign up to volunteer",
      "get involved",
      "join us",
      "help out",
      "i want to help",
      "volunteer",
    ],
    keywords: ["join", "help", "involved", "participate"],
    answer:
      "We'd love your help! There are several ways to get involved:\n\n• **Volunteer at a session or event** — sign up on the Join Us page\n• **Start a chapter** in your city — email us to get started\n• **Sponsor or donate** — funds support free Parkinson's sessions and youth programs\n• **Spread the word** — share our story with your community\n\nVisit Get Involved → Join Us for the volunteer form.",
    suggestions: [
      "How do I start a chapter?",
      "How can I donate?",
      "Tell me about your programs",
    ],
  },
  {
    id: "donate",
    phrases: ["how to donate", "how can i donate", "make a donation", "support financially"],
    keywords: ["donate", "donation", "sponsor", "sponsorship", "funding", "give"],
    answer:
      "Donations directly fund free Parkinson's sessions, youth training, and community outreach. The Health Equity Support page has current campaigns and donation links. For sponsorship discussions, email **info@sportsforequity.live**.",
    suggestions: [
      "What does the money go toward?",
      "Who are your partners?",
      "Tell me about Health Equity Support",
    ],
  },
  {
    id: "research",
    phrases: ["your research", "research page", "table tennis research"],
    keywords: ["research", "studies", "science", "evidence", "published"],
    answer:
      "Our **Research** page highlights peer-reviewed studies and findings on table tennis as a therapeutic activity — particularly for Parkinson's, cognitive health, and inclusion. We use this evidence base to shape our program design.",
    suggestions: [
      "Tell me about the Parkinson's program",
      "What's your impact?",
      "Who are your partners?",
    ],
  },
  {
    id: "partners",
    phrases: ["your partners", "who do you partner with", "partner organizations"],
    keywords: ["partners", "partner", "partnership", "sponsors", "collaborations"],
    answer:
      "Our community partners include:\n\n• **American Parkinson Disease Association (APDA)** — Northwest Chapter\n• **NeuroPong** — Table tennis for MS, Parkinson's & Dementia\n• **Ping Pong Parkinson** (Washington Chapter)\n• **Eric Table Tennis** — Redmond, WA academy\n• **Paddle Palace** — equipment supplier\n• **VT Seva** — global service nonprofit\n• **TimeForYouWellness** — Parkinson's wellness coaching\n\nFull list with links is on the Home page under Community Partners.",
    suggestions: [
      "How can I partner with you?",
      "Tell me about Parkinson's research",
      "How do I donate?",
    ],
  },
  {
    id: "impact",
    phrases: ["your impact", "impact numbers", "how many people", "results"],
    keywords: ["impact", "metrics", "stats", "statistics", "numbers", "reached"],
    answer:
      "Our **Impact to date**:\n\n• **72** Parkinson's sessions delivered\n• **12** national athletes mentored\n• **27** youth athletes empowered\n• **10** active chapters\n\nThe Impact and Impact Stories pages have detailed stories from participants and families.",
    suggestions: [
      "Tell me about Impact Stories",
      "Where are your chapters?",
      "How can I support this?",
    ],
  },
  {
    id: "impact-stories",
    phrases: ["impact stories", "success stories", "patient stories", "testimonials"],
    keywords: ["stories", "testimonials", "testimonial", "experience"],
    answer:
      "The **Impact Stories** page shares first-person accounts from program participants, families, and contributors — including stories from healthcare providers like Dr. Siva Avula MD. These stories show the human side of what the numbers represent.",
    suggestions: [
      "What's your overall impact?",
      "Tell me about the Parkinson's program",
      "How can I share my story?",
    ],
  },
  {
    id: "un-sdg",
    phrases: ["united nations", "sustainable development", "sdg", "un goals"],
    keywords: ["un", "sdg", "sustainable", "goals"],
    answer:
      "Our initiatives are aligned with the **United Nations Sustainable Development Goals** — specifically SDG 3 (Good Health and Well-Being) and SDG 10 (Reduced Inequalities). The Home page links directly to the relevant UN goal pages.",
    suggestions: [
      "What is your mission?",
      "What's your impact?",
      "Tell me about your programs",
    ],
  },
  {
    id: "bot-capability",
    phrases: [
      "what can you do",
      "what can you help with",
      "what do you know",
      "are you a bot",
      "are you ai",
      "how do you work",
    ],
    keywords: ["bot", "robot", "ai"],
    answer:
      "I'm SportsForEquity's site assistant. I can help you learn about our **programs**, **chapters**, **events**, **team**, **partners**, and how to **volunteer or donate**. Ask me anything — or pick a suggestion below to get started.",
    suggestions: [
      "What is SportsForEquity?",
      "What programs do you offer?",
      "How do I volunteer?",
    ],
  },
  {
    id: "thanks",
    phrases: ["thank you", "thanks a lot", "appreciate it", "thank u"],
    keywords: ["thanks", "thank"],
    answer: "You're very welcome! Anything else I can help you with?",
    suggestions: [
      "Tell me about your programs",
      "How can I get involved?",
      "Where are your chapters?",
    ],
  },
  {
    id: "farewell",
    phrases: ["good bye", "see you", "talk later", "got to go", "bye", "goodbye"],
    keywords: [],
    answer:
      "Thanks for stopping by! Reach out at **info@sportsforequity.live** anytime. Have a great day! 👋",
    suggestions: [],
  },
  {
    id: "register-help",
    phrases: [
      "register",
      "registration",
      "sign up",
      "signup",
    ],
    keywords: [],
    answer:
      "Happy to help you register! Could you let me know what you'd like to register for?\n\n" +
      "• **Parkinson's Session** — to attend an in-person table tennis session\n" +
      "• **Volunteer** — to help with sessions, events, or community outreach",
    suggestions: [
      "Register for a Parkinson's Session",
      "Register to volunteer",
    ],
  },
];

// ─── Matching ───────────────────────────────────────────────────────────────
const findBestIntent = (userText) => {
  const normText = " " + normalize(userText) + " ";
  const userTokens = new Set(tokens(userText));

  let best = { intent: null, score: 0 };

  for (const intent of INTENTS) {
    let score = 0;

    // Phrase matches (high weight, longer phrases score more)
    for (const phrase of intent.phrases || []) {
      const normPhrase = " " + normalize(phrase) + " ";
      if (normText.includes(normPhrase)) {
        score += 2 + normalize(phrase).split(" ").length;
      }
    }

    // Keyword matches (lower weight)
    for (const kw of intent.keywords || []) {
      const normKw = normalize(kw);
      if (userTokens.has(normKw)) {
        score += 1;
      }
    }

    if (score > best.score) {
      best = { intent, score };
    }
  }

  return best;
};

const FALLBACK_RESPONSE = {
  text:
    "I'm not sure I caught that — but I can help you with information about SportsForEquity's programs, chapters, events, team, or how to get involved. Try one of these:",
  suggestions: [
    "What is SportsForEquity?",
    "Tell me about your programs",
    "Where are your chapters?",
    "How can I volunteer?",
  ],
};

const MIN_CONFIDENCE = 1; // a single specific keyword/phrase is enough

/**
 * Get a bot response for a user message.
 * @param {string} userText — the user's message
 * @param {Array} _history — conversation history (currently unused, available
 *                           for LLM context if you wire one up later)
 * @returns {{ text: string, suggestions: string[] }}
 */
export const getResponse = (userText, _history = []) => {
  // -------------------------------------------------------------------------
  // FUTURE LLM HOOK: to plug in a real LLM, replace this block with
  //   const resp = await fetch('/api/chat', {
  //     method: 'POST',
  //     body: JSON.stringify({ message: userText, history: _history, knowledge: SITE_KNOWLEDGE }),
  //   });
  //   const { text, suggestions } = await resp.json();
  //   return { text, suggestions };
  // and make this function async + update ChatBot.js to await it.
  // -------------------------------------------------------------------------

  if (!userText || !userText.trim()) {
    return FALLBACK_RESPONSE;
  }

  const { intent, score } = findBestIntent(userText);

  if (intent && score >= MIN_CONFIDENCE) {
    return {
      text: intent.answer,
      suggestions: intent.suggestions || [],
    };
  }

  return FALLBACK_RESPONSE;
};

/**
 * Get the welcome message shown when the chat opens for the first time.
 * Suggestions are tailored to the current route for relevance.
 */
export const getWelcomeMessage = (pathname = "/") => {
  const baseGreeting =
    "Hi! 👋 I'm the SportsForEquity assistant. Ask me anything about our programs, chapters, events, or how to get involved.";

  let suggestions = [
    "What is SportsForEquity?",
    "Tell me about your programs",
    "How can I volunteer?",
  ];

  // Route-aware suggestions
  if (pathname.includes("ParkinsonSession") || pathname.includes("parkinson")) {
    suggestions = [
      "Tell me about the Parkinson's program",
      "Where are the sessions held?",
      "How do I register?",
    ];
  } else if (pathname.includes("YouthEmpowerment")) {
    suggestions = [
      "What is Youth Empowerment?",
      "Who can join?",
      "Tell me about your chapters",
    ];
  } else if (pathname.includes("HealthEquitySupport")) {
    suggestions = [
      "What is Health Equity Support?",
      "How can I donate?",
      "Who are your partners?",
    ];
  } else if (pathname.includes("CommunityOutreach")) {
    suggestions = [
      "What is Community Outreach?",
      "How do I attend an event?",
      "Tell me about your chapters",
    ];
  } else if (pathname.includes("Events")) {
    suggestions = [
      "What events are coming up?",
      "Where are your chapters?",
      "How do I register?",
    ];
  } else if (pathname.includes("core-team") || pathname.includes("advisory")) {
    suggestions = [
      "Who's on the core team?",
      "Who's on the advisory board?",
      "How can I join the team?",
    ];
  } else if (pathname.includes("impact")) {
    suggestions = [
      "What's your impact so far?",
      "Tell me about Impact Stories",
      "How can I support this?",
    ];
  } else if (pathname.includes("contact")) {
    suggestions = [
      "How can I volunteer?",
      "How do I partner with you?",
      "How can I donate?",
    ];
  }

  return { text: baseGreeting, suggestions };
};